/**
 * coppaApi — the typed surface over the COPPA age + under-13 egress-consent
 * backend (POST-AIQ-151 COPPA-PR1/PR2), plus the envelope-aware error readers
 * the global age/consent interceptor and its screens branch on.
 *
 * WHY ITS OWN MODULE: the COPPA gates are CROSS-CUTTING — a 426 `age_required`
 * or a 403 `consent_required` can come back from ANY gated route (the tutor,
 * Learn, Prep, the advisors, Career), not one product surface. So the
 * classification + the two write endpoints (`POST /auth/age`,
 * `POST/DELETE /platform/consent`) live here, separate from any one product's
 * api module, and the response interceptor (ageConsentInterceptor.js) imports
 * them to degrade every gated route gracefully.
 *
 * THE ERROR ENVELOPE (the contract everything here keys on): the backend wraps
 * EVERY error via errors.py `setup_exception_handlers` as
 *   { "error": { "code": <str>, "message": <str>, "details"?: <obj|list> } }
 * There is NO top-level `detail` under this envelope (the older
 * careers/admissions `classify*Error` helpers read `data.detail`, which is
 * `undefined` here — they survive only on their status branch). Read the code,
 * message, and scope from `data.error.*` via the helpers below so a contract
 * read happens in ONE place.
 *
 * THE THREE COPPA SIGNALS (post H4 error-contract unify #68 — every gate now
 * carries a FIRST-CLASS lowercase machine code; we branch on the CODE, never
 * the human message):
 *   - 426  error.code === 'age_required'                  → DOB-capture prompt.
 *   - 403  error.code === 'consent_required'             → under-13 limited
 *          (+ error.details.scope === 'platform_minor')    mode / parent-invite.
 *   - 403  error.code === 'career_access_denied'         → the Career access
 *          on a /careers/ path                              flow (handled in
 *                                                            career/, not here).
 */
import axios from 'axios';
import { API_URL, HELD_REQUEST_TIMEOUT_MS } from '../config';
import { friendlyError as sharedFriendlyError } from '../api/httpError';

const AUTH_BASE = `${API_URL}/api/v1/auth`;
const PLATFORM_BASE = `${API_URL}/api/v1/platform`;

// ── Envelope-aware error readers ─────────────────────────────────────────────

/**
 * The machine `code` from the structured error envelope, or null. This is the
 * field the COPPA branches switch on (age_required / consent_required /
 * career_access_denied). Defensive against a non-enveloped error
 * (network/timeout → no response) so callers can `=== 'age_required'` without
 * optional-chaining.
 */
export function errorCode(err) {
  const code = err?.response?.data?.error?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * The human `message` from the structured error envelope, or null. Used to
 * surface a friendly line when one is provided. (The Career composite-gate 403
 * is now disambiguated by its first-class code `career_access_denied` in
 * career/careersApi.js, not by this message — H4 unify #68.)
 */
export function errorMessage(err) {
  const msg = err?.response?.data?.error?.message;
  return typeof msg === 'string' ? msg : null;
}

/**
 * The `details.scope` from the structured error envelope, or null. The
 * `consent_required` 403 carries `{ scope: 'platform_minor' }` so the screen
 * knows which scope a guardian must grant. `details` can also be a LIST (the
 * 422 validation shape) — guard for the object case.
 */
export function errorScope(err) {
  const details = err?.response?.data?.error?.details;
  if (details && !Array.isArray(details) && typeof details === 'object') {
    return typeof details.scope === 'string' ? details.scope : null;
  }
  return null;
}

/**
 * A user-friendly message for a write/validation failure, NEVER a raw error
 * object or stack. Delegates to the shared canonical reader (../api/httpError)
 * so the COPPA write screens (DOB capture, parent-invite) surface errors
 * IDENTICALLY to every other product surface (H4 unify #68): envelope
 * `error.message` first, legacy top-level `detail`, then a Pydantic-422 list,
 * then the default. Re-exported under this name so coppa's callers need no
 * import change.
 */
export function friendlyError(err, fallback) {
  return sharedFriendlyError(err, fallback);
}

// ── Gate classifiers ─────────────────────────────────────────────────────────

/**
 * True for a 426 `age_required` — the NULL-DOB DOB-capture prompt.
 *
 * CODE CASING (H4 unify #68): the canonical code is now LOWERCASE `age_required`
 * (the AgeRequired AppException carries it first-class; before the unify it was
 * the uppercase `AGE_REQUIRED` derived by errors.py's status→code map). We
 * accept BOTH for ONE release so the global age interceptor keeps retrying the
 * DOB-capture flow whether the client is talking to a new or an old backend
 * during the rollout. Drop the uppercase arm once the backend is fully deployed.
 * The 426 status alone is also unambiguous (AgeRequired is the only 426 the API
 * emits), so this stays correct even if a future code rename slips the casing.
 */
export function isAgeRequired(err) {
  if (err?.response?.status !== 426) return false;
  const code = errorCode(err);
  return code === 'age_required' || code === 'AGE_REQUIRED';
}

/**
 * True for the under-13 `platform_minor` 403 — the open-section egress freeze
 * (limited mode until a guardian grants). Keyed on the AppException code, not
 * the status alone (a generic 403 must NOT trip the parent-invite screen).
 */
export function isPlatformConsentRequired(err) {
  return (
    err?.response?.status === 403 && errorCode(err) === 'consent_required'
  );
}

// ── Write endpoints ──────────────────────────────────────────────────────────

/**
 * Write-once capture of the caller's date of birth (POST /auth/age). The target
 * of the 426 `age_required` prompt for the existing NULL-DOB base.
 *
 * MINIMIZATION: `dob` is sent, never persisted client-side or logged — the
 * backend does not echo it back, and neither do we. Returns the bare ack
 * `{status:'ok'}` on success. A 409 means already-set (write-once); the caller
 * treats that as "DOB is on file, proceed" (it shouldn't happen on the normal
 * capture path, but a double-submit / a value set in another tab lands here).
 *
 * NOT tagged x-background-request: this is a foreground, user-initiated write;
 * a 401 here legitimately means the session died and should surface.
 *
 * Bounded by HELD_REQUEST_TIMEOUT_MS: the age gate is held open until this
 * answers, with every gated request waiting behind it. A write the server
 * finishes after the client gave up comes back as the 409 on the retry.
 */
export async function setDateOfBirth(dob) {
  const res = await axios.post(
    `${AUTH_BASE}/age`,
    { date_of_birth: dob },
    { timeout: HELD_REQUEST_TIMEOUT_MS },
  );
  return res.data;
}

/**
 * A VERIFIED guardian grants the under-13 `platform_minor` egress scaffold for
 * one minor (POST /platform/consent). `scope` + `policy_version` are FORCED
 * server-side (a guardian-link grant can't be mislabeled real VPC), so the body
 * is just `{ student_id }`. Returns the resulting ConsentRecord.
 *
 * The whole surface is flag-gated (`platform_minor_consent_enabled`): a 404
 * here means the grant path is dark-launched — the screen surfaces a
 * "coming soon / contact support" state rather than a dead error. (The egress
 * FREEZE is unconditional, so a 404 here does NOT mean the child can egress.)
 */
export async function grantPlatformConsent(studentId) {
  const res = await axios.post(`${PLATFORM_BASE}/consent`, {
    student_id: studentId,
  });
  return res.data;
}

/** Revoke `platform_minor` — a verified guardian OR the student themselves. */
export async function revokePlatformConsent(studentId) {
  // axios DELETE with a body must pass it under `data`.
  const res = await axios.delete(`${PLATFORM_BASE}/consent`, {
    data: { student_id: studentId },
  });
  return res.data;
}
